/**
 * PayrollTab — Phase 4 Sub-Pass 2.
 *
 * Renders the four payroll sub-tabs (Compensation, Federal W-4, State Withholding,
 * Banking) for an employee. The parent <EmployeeDetail/> only mounts this when
 * the user has the `payroll` HR grant (see EmployeeDetail.jsx canSeePayrollTab).
 *
 * Each sub-tab follows the effective-dated UX:
 *   • Current record prominent
 *   • History below (read-only timeline)
 *   • "Add new" creates a NEW effective-dated record — never overwrites
 *
 * Sensitive fields (pay_rate, account_number, routing_number) use SensitiveField
 * — the redacted display + audited reveal endpoint. The component never holds
 * plaintext beyond the reveal-then-auto-hide window.
 *
 * Executive-comp wall: rows with is_executive=true display a "restricted"
 * indicator and disable the reveal button unless the caller has the
 * executive_compensation grant. The backend still rejects the reveal regardless.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, DollarSign, FileText, MapPin, Landmark, Lock, History, ShieldAlert, Trash2, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI, federalW4API, stateWithholdingAPI, bankingAPI, payrollAPI } from '../../lib/api';
import { extractErrorMessage } from '../../lib/errors';
import { settle } from '../../lib/useResilientLoad';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import SensitiveField from '../../components/auth/SensitiveField';
import AccessState from '../../components/auth/AccessState';
import RequireCap from '../../components/auth/RequireCap';
import { useSearchParams } from 'react-router-dom';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const NO_TAX_STATES = new Set(['AK','FL','NV','NH','SD','TN','TX','WA','WY']);
const PAY_REASONS = ['New Hire','Raise','License Change','EWW Rate','OH HR','TX HR','Promotion','Demotion','Annual Adjustment','None'];
const FILING_STATUSES_W4 = ['Single or Married filing separately','Married filing jointly or Qualifying surviving spouse','Head of household'];
const PAY_TYPES = ['Hourly','Salary'];
const EMP_TYPES = ['Full-Time','Part-Time','Temporary'];
const ACCRUAL_TYPES = ['Biweekly','Semi-Monthly','Weekly','Monthly'];
const ACCOUNT_TYPES = ['Checking','Savings','Pay Card'];
const ALLOCATION_TYPES = ['All','Partial','Remainder'];

function formatDate(d) {
  if (!d) return '—';
  // Date-only strings (YYYY-MM-DD) must show the calendar day the backend meant,
  // never shifted west of UTC — build a local date from the parts (item 7).
  try {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d).trim());
    const date = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return d; }
}
function formatCurrency(v) {
  if (v == null || v === '') return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─────────────────────────────────────────────────────────────────────────
// COMPENSATION SUB-TAB
// ─────────────────────────────────────────────────────────────────────────
function CompensationSubtab({ employeeId, perms }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const canSeeExecComp = perms.hasGrant ? perms.hasGrant('executive_compensation') : false;

  const load = useCallback(async () => {
    setLoading(true);
    const [r] = await settle([compensationAPI.list(employeeId)]);
    if (r.ok) setRecords(r.data || []);
    else if (r.denied) setDenied(true);
    setLoading(false);
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  const current = records.find(r => r.effective_date <= today) || null;
  const history = records.filter(r => r !== current);

  const submit = async () => {
    if (!form.effective_date || !form.pay_rate || !form.pay_type || !form.pay_info_reason) {
      toast.error('Effective date, pay rate, pay type, and pay info reason are required');
      return;
    }
    setSaving(true);
    try {
      await compensationAPI.create(employeeId, {
        ...form,
        pay_rate: parseFloat(form.pay_rate),
        employee_type: form.employee_type || 'Full-Time',
      });
      toast.success('New compensation record created');
      setShowAdd(false);
      setForm({});
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to save'));
    } finally { setSaving(false); }
  };

  // Dialog lives OUTSIDE AccessState. When the list is empty AccessState short-
  // circuits to its empty card and does not render `children`, so any Dialog
  // declared inside would never be in the DOM → "Add first record" was a dead
  // click. Lifting it to a sibling fixes the silent-no-op.
  const AddDialog = (
    <Dialog open={showAdd} onOpenChange={setShowAdd}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New Compensation Record</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Effective Date *</Label><Input type="date" value={form.effective_date || ''} onChange={e => setForm({ ...form, effective_date: e.target.value })} data-testid="comp-form-effective-date" /></div>
          <div><Label>Pay Rate *</Label><Input type="number" step="0.01" value={form.pay_rate || ''} onChange={e => setForm({ ...form, pay_rate: e.target.value })} placeholder="0.00" data-testid="comp-form-pay-rate" /></div>
          <div><Label>Pay Type *</Label><Select value={form.pay_type || ''} onValueChange={v => setForm({ ...form, pay_type: v })}><SelectTrigger data-testid="comp-form-pay-type"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{PAY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Employee Type *</Label><Select value={form.employee_type || ''} onValueChange={v => setForm({ ...form, employee_type: v })}><SelectTrigger data-testid="comp-form-emp-type"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{EMP_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Accrual Type</Label><Select value={form.accrual_type || ''} onValueChange={v => setForm({ ...form, accrual_type: v })}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{ACCRUAL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Pay Info Reason *</Label><Select value={form.pay_info_reason || ''} onValueChange={v => setForm({ ...form, pay_info_reason: v })}><SelectTrigger data-testid="comp-form-reason"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{PAY_REASONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <div className="md:col-span-2 flex items-center gap-2">
            <input type="checkbox" id="comp-flsa" checked={!!form.flsa_exempt} onChange={e => setForm({ ...form, flsa_exempt: e.target.checked })} />
            <Label htmlFor="comp-flsa">FLSA Exempt</Label>
            {canSeeExecComp && (
              <>
                <input type="checkbox" id="comp-exec" className="ml-4" checked={!!form.is_executive} onChange={e => setForm({ ...form, is_executive: e.target.checked })} data-testid="comp-form-is-executive" />
                <Label htmlFor="comp-exec" className="text-amber-900">Executive compensation (walled)</Label>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="comp-form-submit">{saving ? 'Saving…' : 'Create Record'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
    {AddDialog}
    <AccessState module="compensation" action="view" items={records} loading={loading} denied={denied} entityLabel="compensation records"
      emptyAction={
        <RequireCap module="compensation" action="add">
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="comp-add-first-btn"><Plus className="w-4 h-4 mr-1.5" /> Add first compensation record</Button>
        </RequireCap>
      }>
      <div className="space-y-4" data-testid="compensation-subtab">
        {/* Header + add button */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-600" /> Compensation</h3>
            <p className="text-sm text-muted-foreground">Effective-dated history. Each pay change is its own record — none is ever overwritten.</p>
          </div>
          <RequireCap module="compensation" action="add">
            <Button size="sm" onClick={() => setShowAdd(true)} data-testid="comp-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Record</Button>
          </RequireCap>
        </div>

        {/* Current */}
        {current && (
          <Card data-testid="comp-current-card" className="border-emerald-200 bg-emerald-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                Current
                <Badge variant="outline" className="text-xs">Effective {formatDate(current.effective_date)}</Badge>
                {current.is_executive && <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-xs"><ShieldAlert className="w-3 h-3 mr-1" />Executive Comp</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {current.is_executive && !canSeeExecComp ? (
                <div className="md:col-span-3 flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900" data-testid="comp-exec-wall">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">Executive compensation record — values restricted to executive-compensation grant holders.</span>
                </div>
              ) : (
                <>
                  <div>
                    <SensitiveField
                      testId="comp-pay-rate"
                      label="Pay Rate"
                      module="compensation"
                      hrGrant={current.is_executive ? 'executive_compensation' : 'payroll'}
                      redactedValue="••••••"
                      reveal={() => compensationAPI.reveal(employeeId, current.id).then(r => formatCurrency(r.data?.pay_rate))}
                      format="currency"
                      description="Audit-logged. Auto-hides after 30s."
                    />
                  </div>
                  <div><Label>Pay Type</Label><p className="font-medium pt-1">{current.pay_type || '—'}</p></div>
                  <div><Label>Employee Type</Label><p className="font-medium pt-1">{current.employee_type || '—'}</p></div>
                  <div><Label>Reason</Label><p className="font-medium pt-1">{current.pay_info_reason || '—'}</p></div>
                  <div><Label>Accrual</Label><p className="font-medium pt-1">{current.accrual_type || '—'}</p></div>
                  <div><Label>FLSA Exempt</Label><p className="font-medium pt-1">{current.flsa_exempt ? 'Yes' : 'No'}</p></div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* History */}
        {history.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground"><History className="w-4 h-4" /> History ({history.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {history.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 border rounded-lg text-sm" data-testid={`comp-history-row-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs">{formatDate(r.effective_date)}</Badge>
                    {r.is_executive && <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-xs"><ShieldAlert className="w-3 h-3 mr-1" />Exec</Badge>}
                    <span className="text-muted-foreground">{r.pay_info_reason} • {r.pay_type} • {r.employee_type}</span>
                  </div>
                  {r.is_executive && !canSeeExecComp ? (
                    <Badge variant="outline" className="text-amber-700 border-amber-300"><Lock className="w-3 h-3 mr-1" />Restricted</Badge>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={async () => {
                      try { const v = await compensationAPI.reveal(employeeId, r.id); toast.success(`Pay rate: ${formatCurrency(v.data?.pay_rate)}`, { duration: 8000 }); }
                      catch (e) { toast.error(extractErrorMessage(e, 'Failed to reveal')); }
                    }} data-testid={`comp-history-reveal-${r.id}`}>Reveal</Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Add Record Dialog lifted to a sibling of AccessState above. */}
      </div>
    </AccessState>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// FEDERAL W-4 SUB-TAB
// ─────────────────────────────────────────────────────────────────────────
function FederalW4Subtab({ employeeId }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ tax_year: new Date().getFullYear(), claim_exemption: false, multiple_jobs: false, qualifying_children: 0, other_dependents: 0, other_tax_credits: 0, other_income: 0, deductions: 0, extra_withholding: 0 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [r] = await settle([federalW4API.list(employeeId)]);
    if (r.ok) setRecords(r.data || []);
    else if (r.denied) setDenied(true);
    setLoading(false);
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  const current = records.find(r => r.effective_date <= today) || null;
  const history = records.filter(r => r !== current);

  const submit = async () => {
    if (!form.effective_date || !form.anticipated_filing_status) {
      toast.error('Effective date and filing status are required');
      return;
    }
    setSaving(true);
    try {
      await federalW4API.create(employeeId, form);
      toast.success('New W-4 record created');
      setShowAdd(false);
      setForm({ tax_year: new Date().getFullYear(), claim_exemption: false, multiple_jobs: false, qualifying_children: 0, other_dependents: 0, other_tax_credits: 0, other_income: 0, deductions: 0, extra_withholding: 0 });
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to save'));
    } finally { setSaving(false); }
  };

  return (
    <>
    <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>New Federal W-4 Record</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Effective Date *</Label><Input type="date" value={form.effective_date || ''} onChange={e => setForm({ ...form, effective_date: e.target.value })} /></div>
              <div><Label>Tax Year *</Label><Input type="number" min="2020" max="2100" value={form.tax_year} onChange={e => setForm({ ...form, tax_year: parseInt(e.target.value) })} /></div>
              <div className="md:col-span-2"><Label>Filing Status *</Label><Select value={form.anticipated_filing_status || ''} onValueChange={v => setForm({ ...form, anticipated_filing_status: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{FILING_STATUSES_W4.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div className="md:col-span-2 flex items-center gap-4">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.claim_exemption} onChange={e => setForm({ ...form, claim_exemption: e.target.checked })} /> Claim Exemption</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.multiple_jobs} onChange={e => setForm({ ...form, multiple_jobs: e.target.checked })} /> Multiple Jobs (Step 2c)</label>
              </div>
              <div><Label>Qualifying Children</Label><Input type="number" min="0" value={form.qualifying_children} onChange={e => setForm({ ...form, qualifying_children: parseInt(e.target.value || 0) })} /></div>
              <div><Label>Other Dependents</Label><Input type="number" min="0" value={form.other_dependents} onChange={e => setForm({ ...form, other_dependents: parseInt(e.target.value || 0) })} /></div>
              <div><Label>Other Income (4a)</Label><Input type="number" step="0.01" value={form.other_income} onChange={e => setForm({ ...form, other_income: parseFloat(e.target.value || 0) })} /></div>
              <div><Label>Deductions (4b)</Label><Input type="number" step="0.01" value={form.deductions} onChange={e => setForm({ ...form, deductions: parseFloat(e.target.value || 0) })} /></div>
              <div><Label>Extra Withholding (4c)</Label><Input type="number" step="0.01" value={form.extra_withholding} onChange={e => setForm({ ...form, extra_withholding: parseFloat(e.target.value || 0) })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Create Record'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    <AccessState module="federal_w4" action="view" items={records} loading={loading} denied={denied} entityLabel="W-4 records"
      emptyAction={<RequireCap module="federal_w4" action="add"><Button size="sm" onClick={() => setShowAdd(true)} data-testid="w4-add-first-btn"><Plus className="w-4 h-4 mr-1.5" /> Add first W-4</Button></RequireCap>}>
      <div className="space-y-4" data-testid="federal-w4-subtab">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Federal W-4</h3>
            <p className="text-sm text-muted-foreground">Effective-dated W-4 elections. Step-3 dependent amounts auto-compute from the tax-year config.</p>
          </div>
          <RequireCap module="federal_w4" action="add"><Button size="sm" onClick={() => setShowAdd(true)} data-testid="w4-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Record</Button></RequireCap>
        </div>

        {current && (
          <Card data-testid="w4-current-card" className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                Current
                <Badge variant="outline" className="text-xs">Effective {formatDate(current.effective_date)}</Badge>
                <Badge variant="outline" className="text-xs">Tax Year {current.tax_year}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div><Label>Filing Status</Label><p className="font-medium pt-1">{current.anticipated_filing_status}</p></div>
              <div><Label>Multiple Jobs (Step 2)</Label><p className="font-medium pt-1">{current.multiple_jobs ? 'Yes' : 'No'}</p></div>
              <div><Label>Claim Exemption</Label><p className="font-medium pt-1">{current.claim_exemption ? 'Yes' : 'No'}</p></div>
              <div><Label>Total Dependent Amount (Step 3)</Label><p className="font-medium pt-1">{formatCurrency(current.total_dependent_amount)}</p></div>
              <div><Label>Other Income (4a)</Label><p className="font-medium pt-1">{formatCurrency(current.other_income)}</p></div>
              <div><Label>Deductions (4b)</Label><p className="font-medium pt-1">{formatCurrency(current.deductions)}</p></div>
              <div><Label>Extra Withholding (4c)</Label><p className="font-medium pt-1">{formatCurrency(current.extra_withholding)}</p></div>
            </CardContent>
          </Card>
        )}

        {history.length > 0 && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2 text-muted-foreground"><History className="w-4 h-4" /> History ({history.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {history.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 border rounded-lg text-sm" data-testid={`w4-history-row-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{formatDate(r.effective_date)}</Badge>
                    <span className="text-muted-foreground">TY {r.tax_year} • {r.anticipated_filing_status}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Add Record Dialog lifted above AccessState. */}
      </div>
    </AccessState>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// STATE WITHHOLDING SUB-TAB
// ─────────────────────────────────────────────────────────────────────────
function StateWithholdingSubtab({ employeeId, workState }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [stateFilter, setStateFilter] = useState(workState || 'all');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ withholding_state: workState || '', state_exemption: false, msrra_exemption: false, allowance_self: 0, allowance_spouse: 0, allowance_dependents: 0, additional_allowances: 0, additional_withholding: 0 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = stateFilter && stateFilter !== 'all' ? stateFilter : undefined;
    const [r] = await settle([stateWithholdingAPI.list(employeeId, params)]);
    if (r.ok) setRecords(r.data || []);
    else if (r.denied) setDenied(true);
    setLoading(false);
  }, [employeeId, stateFilter]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  const byState = {};
  records.forEach(r => {
    const k = r.withholding_state;
    if (!byState[k]) byState[k] = [];
    byState[k].push(r);
  });

  const submit = async () => {
    if (!form.effective_date || !form.withholding_state) {
      toast.error('Effective date and state are required');
      return;
    }
    setSaving(true);
    try {
      await stateWithholdingAPI.create(employeeId, form);
      toast.success(`New ${form.withholding_state} withholding record created`);
      setShowAdd(false);
      setForm({ withholding_state: workState || '', state_exemption: false, msrra_exemption: false, allowance_self: 0, allowance_spouse: 0, allowance_dependents: 0, additional_allowances: 0, additional_withholding: 0 });
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to save'));
    } finally { setSaving(false); }
  };

  return (
    <>
    <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>New State Withholding Record</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Effective Date *</Label><Input type="date" value={form.effective_date || ''} onChange={e => setForm({ ...form, effective_date: e.target.value })} /></div>
              <div><Label>State *</Label><Select value={form.withholding_state || ''} onValueChange={v => setForm({ ...form, withholding_state: v })}><SelectTrigger data-testid="sw-form-state"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}{NO_TAX_STATES.has(s) ? ' (no wage tax)' : ''}</SelectItem>)}</SelectContent></Select></div>
              {!NO_TAX_STATES.has(form.withholding_state) && (
                <>
                  <div><Label>Filing Status</Label><Input value={form.filing_status || ''} onChange={e => setForm({ ...form, filing_status: e.target.value })} placeholder="Single / Married / Head of Household" /></div>
                  <div><Label>Self Allowances</Label><Input type="number" min="0" value={form.allowance_self} onChange={e => setForm({ ...form, allowance_self: parseInt(e.target.value || 0) })} /></div>
                  <div><Label>Spouse Allowances</Label><Input type="number" min="0" value={form.allowance_spouse} onChange={e => setForm({ ...form, allowance_spouse: parseInt(e.target.value || 0) })} /></div>
                  <div><Label>Dependent Allowances</Label><Input type="number" min="0" value={form.allowance_dependents} onChange={e => setForm({ ...form, allowance_dependents: parseInt(e.target.value || 0) })} /></div>
                  <div><Label>Additional Withholding</Label><Input type="number" step="0.01" value={form.additional_withholding} onChange={e => setForm({ ...form, additional_withholding: parseFloat(e.target.value || 0) })} /></div>
                  <div className="md:col-span-2 flex items-center gap-4">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={form.state_exemption} onChange={e => setForm({ ...form, state_exemption: e.target.checked })} /> State exemption</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={form.msrra_exemption} onChange={e => setForm({ ...form, msrra_exemption: e.target.checked })} /> MSRRA exemption</label>
                  </div>
                </>
              )}
              {NO_TAX_STATES.has(form.withholding_state) && (
                <div className="md:col-span-2 p-3 rounded-lg bg-slate-50 border text-sm text-muted-foreground">
                  {form.withholding_state} does not levy a state wage withholding tax. A no-tax marker record will be filed.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Create Record'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    <AccessState module="state_withholding" action="view" items={records} loading={loading} denied={denied} entityLabel="state withholding records"
      emptyAction={<RequireCap module="state_withholding" action="add"><Button size="sm" onClick={() => setShowAdd(true)} data-testid="sw-add-first-btn"><Plus className="w-4 h-4 mr-1.5" /> Add first record</Button></RequireCap>}>
      <div className="space-y-4" data-testid="state-withholding-subtab">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><MapPin className="w-5 h-5 text-purple-600" /> State Withholding</h3>
            <p className="text-sm text-muted-foreground">Effective-dated state W-4 records. Multi-state supported. {workState && <span>Current work state: <strong>{workState}</strong></span>}</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="w-48" data-testid="state-withholding-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                {US_STATES.map(s => <SelectItem key={s} value={s}>{s}{NO_TAX_STATES.has(s) ? ' (no wage tax)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            <RequireCap module="state_withholding" action="add"><Button size="sm" onClick={() => setShowAdd(true)} data-testid="sw-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Record</Button></RequireCap>
          </div>
        </div>

        {Object.entries(byState).map(([state, recs]) => {
          const current = recs.find(r => r.effective_date <= today) || recs[0];
          const history = recs.filter(r => r !== current);
          const noTax = NO_TAX_STATES.has(state);
          return (
            <Card key={state} data-testid={`sw-state-card-${state}`} className={noTax ? 'border-slate-200 bg-slate-50/30' : 'border-purple-200 bg-purple-50/20'}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  {state}
                  {noTax && <Badge variant="outline" className="text-xs">No wage tax</Badge>}
                  {current && <Badge variant="outline" className="text-xs">Effective {formatDate(current.effective_date)}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {noTax ? (
                  <p className="text-muted-foreground">{state} does not levy a state wage withholding tax — no form is required.</p>
                ) : current ? (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div><Label>Filing Status</Label><p className="font-medium">{current.filing_status || '—'}</p></div>
                    <div><Label>Total Allowances</Label><p className="font-medium">{current.total_allowances ?? '—'}</p></div>
                    <div><Label>State Exemption</Label><p className="font-medium">{current.state_exemption ? 'Yes' : 'No'}</p></div>
                    <div><Label>Additional Withholding</Label><p className="font-medium">{formatCurrency(current.additional_withholding)}</p></div>
                    {current.msrra_exemption && <div className="md:col-span-4"><Badge variant="outline" className="text-xs">MSRRA exemption claimed</Badge></div>}
                  </div>
                ) : null}
                {history.length > 0 && (
                  <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                    History: {history.map(h => <Badge key={h.id} variant="outline" className="ml-1 text-xs">{formatDate(h.effective_date)}</Badge>)}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* SW Dialog lifted above AccessState. */}
      </div>
    </AccessState>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// BANKING / DIRECT DEPOSIT SUB-TAB
// ─────────────────────────────────────────────────────────────────────────
function BankingSubtab({ employeeId }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ allocation_type: 'All' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [r] = await settle([bankingAPI.list(employeeId)]);
    if (r.ok) setAccounts(r.data || []);
    else if (r.denied) setDenied(true);
    setLoading(false);
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.bank_name || !form.account_type || !form.account_number || !form.routing_number) {
      toast.error('Bank name, account type, account number and routing number are required');
      return;
    }
    setSaving(true);
    try {
      await bankingAPI.create(employeeId, form);
      toast.success('Banking account added');
      setShowAdd(false);
      setForm({ allocation_type: 'All' });
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to save'));
    } finally { setSaving(false); }
  };

  const remove = async (acct) => {
    if (!confirm(`Delete ${acct.bank_name} ${acct.account_type} ending in ${acct.account_number_last4 || '????'}?`)) return;
    try { await bankingAPI.delete(employeeId, acct.id); toast.success('Account deleted'); await load(); }
    catch (e) { toast.error(extractErrorMessage(e, 'Failed to delete')); }
  };

  return (
    <>
    <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Add Direct-Deposit Account</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Bank Name *</Label><Input value={form.bank_name || ''} onChange={e => setForm({ ...form, bank_name: e.target.value })} data-testid="banking-form-bank-name" /></div>
              <div><Label>Account Type *</Label><Select value={form.account_type || ''} onValueChange={v => setForm({ ...form, account_type: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Account Number *</Label><Input value={form.account_number || ''} onChange={e => setForm({ ...form, account_number: e.target.value.replace(/\D/g, '').slice(0, 17) })} className="font-mono" /></div>
              <div><Label>Routing Number *</Label><Input value={form.routing_number || ''} onChange={e => setForm({ ...form, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) })} className="font-mono" /></div>
              <div className="md:col-span-2">
                <Label>Bank Address <span className="text-xs text-slate-400">(single line — branch address from the packet)</span></Label>
                <Input
                  value={form.bank_address || ''}
                  onChange={e => setForm({ ...form, bank_address: e.target.value })}
                  placeholder="e.g. 1201 W I 35 Frontage Rd Edmond OK 73034"
                  data-testid="banking-form-bank-address"
                />
              </div>
              <div><Label>Allocation Type</Label><Select value={form.allocation_type || 'All'} onValueChange={v => setForm({ ...form, allocation_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ALLOCATION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              {form.allocation_type !== 'All' && form.allocation_type !== 'Remainder' && (
                <div><Label>Allocation Amount</Label><Input type="number" step="0.01" value={form.allocation_amount || ''} onChange={e => setForm({ ...form, allocation_amount: parseFloat(e.target.value || 0) })} /></div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="banking-form-submit">{saving ? 'Saving…' : 'Add Account'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    <AccessState module="banking" action="view" items={accounts} loading={loading} denied={denied} entityLabel="direct deposit accounts"
      emptyAction={<RequireCap module="banking" action="add"><Button size="sm" onClick={() => setShowAdd(true)} data-testid="banking-add-first-btn"><Plus className="w-4 h-4 mr-1.5" /> Add first account</Button></RequireCap>}>
      <div className="space-y-4" data-testid="banking-subtab">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><Landmark className="w-5 h-5 text-indigo-600" /> Direct Deposit</h3>
            <p className="text-sm text-muted-foreground">Multi-account direct-deposit allocations. Account and routing numbers are AES-256 encrypted at rest.</p>
          </div>
          <RequireCap module="banking" action="add"><Button size="sm" onClick={() => setShowAdd(true)} data-testid="banking-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Account</Button></RequireCap>
        </div>

        <div className="space-y-3">
          {accounts.map(a => (
            <Card key={a.id} data-testid={`banking-account-${a.id}`}>
              <CardContent className="pt-6 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-base">{a.bank_name}</h4>
                    {a.is_active === false && (
                      <Badge variant="outline" className="mt-1 text-xs" data-testid={`banking-inactive-${a.id}`}>
                        Inactive — {a.deactivated_reason || 'no longer on file'}
                      </Badge>
                    )}
                    {a.bank_address && <p className="text-xs text-slate-500 mt-0.5" data-testid={`banking-bank-address-${a.id}`}>{a.bank_address}</p>}
                    <p className="text-sm text-muted-foreground">{a.account_type} • {a.allocation_type || 'All'}{a.allocation_amount ? ` ${formatCurrency(a.allocation_amount)}` : ''}</p>
                  </div>
                  <RequireCap module="banking" action="delete">
                    <Button size="sm" variant="ghost" onClick={() => remove(a)} className="text-rose-600" data-testid={`banking-delete-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </RequireCap>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <SensitiveField
                    testId={`banking-account-num-${a.id}`}
                    label="Account Number"
                    module="banking"
                    hrGrant="payroll"
                    redactedValue={a.account_number_last4 ? `••••••${a.account_number_last4}` : null}
                    reveal={() => bankingAPI.reveal(employeeId, a.id).then(r => r.data?.account_number)}
                    format="account"
                    description="Audit-logged. Requires payroll grant. Auto-hides after 30s."
                  />
                  <SensitiveField
                    testId={`banking-routing-${a.id}`}
                    label="Routing Number"
                    module="banking"
                    hrGrant="payroll"
                    redactedValue={a.routing_number_last4 ? `•••••${a.routing_number_last4}` : null}
                    reveal={() => bankingAPI.reveal(employeeId, a.id).then(r => r.data?.routing_number)}
                    format="routing"
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Banking Dialog lifted above AccessState. */}
      </div>
    </AccessState>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PAY DISTRIBUTION + PAY FREQUENCY PANEL
// ─────────────────────────────────────────────────────────────────────────
// These two fields live on the Employee record itself (paycheck_distribution_description
// and paycheck_frequency_description) and are persisted by the parent EmployeeDetail
// global Save button. Surfacing them here keeps payroll-related setup co-located with
// Compensation / W-4 / State Withholding / Banking and inherits the parent's `payroll`
// HR-grant gate (the Payroll tab is only mounted when canSeePayrollTab is true).
function PayDistFreqPanel({ employee, setEmployee }) {
  if (!employee || !setEmployee) return null;
  // Pay distribution presets — alphabetical with Other at bottom. 'ACH' is
  // the modern direct-deposit standard and the default for new employees
  // (see EmployeeDetail.jsx initial state). Kept here so the dropdown stays
  // a single source of truth.
  const presets = ['ACH', 'Delivered by Supervisor', 'Mail', 'Pickup'];
  const current = employee.paycheck_distribution_description || '';
  const userOther = !!employee._pay_dist_other_active;
  const displayValue = presets.includes(current) ? current : (userOther ? '__other__' : '');
  return (
    <Card className="border-blue-100 bg-blue-50/20 mb-4" data-testid="payroll-pay-setup-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><DollarSign className="w-4 h-4 text-blue-600" /> Pay Setup</CardTitle>
        <CardDescription>How and how often this employee is paid. Saved with the employee header Save.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Pay Distribution</Label>
            <Select
              value={displayValue}
              onValueChange={(v) => {
                if (v === '__other__') {
                  setEmployee({ ...employee, paycheck_distribution_description: '', _pay_dist_other_active: true });
                } else {
                  setEmployee({ ...employee, paycheck_distribution_description: v, _pay_dist_other_active: false });
                }
              }}
            >
              <SelectTrigger data-testid="payroll-pay-dist"><SelectValue placeholder="Select distribution method" /></SelectTrigger>
              <SelectContent>
                {presets.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                <SelectItem value="__other__">Other…</SelectItem>
              </SelectContent>
            </Select>
            {userOther && (
              <Input
                value={current}
                onChange={(e) => setEmployee({ ...employee, paycheck_distribution_description: e.target.value })}
                placeholder="Specify distribution method"
                data-testid="payroll-pay-dist-other"
              />
            )}
          </div>
          <div className="space-y-2">
            <Label>Pay Frequency</Label>
            <Select
              value={employee.paycheck_frequency_description || ''}
              onValueChange={(v) => setEmployee({ ...employee, paycheck_frequency_description: v })}
            >
              <SelectTrigger data-testid="payroll-pay-freq"><SelectValue placeholder="Select frequency" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Monthly">Monthly</SelectItem>
                <SelectItem value="Semi-Monthly">Semi-Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PAY GROUPS & RATES SUB-TAB (v4.36.0 parallel-test setup, effective-dated)
// ─────────────────────────────────────────────────────────────────────────
function PayGroupsRatesSubtab({ employeeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [r] = await settle([payrollAPI.employeePayrollSetup(employeeId)]);
    if (r.ok) setData(r.data);
    else if (r.denied) setDenied(true);
    setLoading(false);
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-8 text-center text-sm text-muted-foreground" data-testid="paygroups-loading">Loading…</div>;
  if (denied) return <div className="py-8 text-center text-sm text-amber-700" data-testid="paygroups-denied">You don't have access to payroll setup for this employee.</div>;
  if (!data) return null;

  const Section = ({ title, icon, rows, render, testId, empty }) => {
    const today = new Date().toISOString().slice(0, 10);
    const currentIdx = rows.findIndex((r) => (r.effective_date || '') <= today);
    const current = currentIdx >= 0 ? rows[currentIdx] : null;
    const scheduled = rows.slice(0, currentIdx < 0 ? rows.length : currentIdx); // future-dated
    const history = currentIdx >= 0 ? rows.slice(currentIdx + 1) : [];
    return (
      <Card data-testid={testId}>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2">{icon}{title}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {rows.length === 0 ? <p className="text-sm text-muted-foreground">{empty}</p> : (
            <>
              {scheduled.map((r, i) => (
                <div key={`s${i}`} className="p-3 border border-amber-200 bg-amber-50/40 rounded-lg text-sm" data-testid={`${testId}-scheduled-${i}`}>
                  <Badge variant="outline" className="text-xs mr-2">Scheduled · {formatDate(r.effective_date)}</Badge>
                  {render(r)}
                </div>
              ))}
              {current && (
                <div className="p-3 border border-emerald-200 bg-emerald-50/40 rounded-lg text-sm" data-testid={`${testId}-current`}>
                  <Badge variant="outline" className="text-xs mr-2">Current · {formatDate(current.effective_date)}</Badge>
                  {render(current)}
                </div>
              )}
              {history.length > 0 && (
                <div className="pt-1">
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1"><History className="w-3 h-3" /> History ({history.length})</p>
                  {history.map((r, i) => (
                    <div key={`h${i}`} className="flex items-center justify-between p-2 border rounded text-xs mb-1" data-testid={`${testId}-history-${i}`}>
                      <span><Badge variant="outline" className="text-xs mr-2">{formatDate(r.effective_date)}</Badge>{render(r)}</span>
                      <span className="text-muted-foreground">by {r.set_by || '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="paygroups-rates-subtab">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2"><Layers className="w-5 h-5 text-indigo-600" /> Pay Groups &amp; Rates</h3>
        <p className="text-sm text-muted-foreground">Pay group, insurance classification and officer pay rates used by the payroll parallel test. Effective-dated — nothing is overwritten.</p>
      </div>
      {data.restricted && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm" data-testid="paygroups-exec-wall">
          <Lock className="w-4 h-4" /> {data.restricted_note || 'Executive compensation employee — restricted to Exec Comp grant holders.'}
        </div>
      )}
      <Section title="Pay Group" testId="paygroup-section" empty="No pay group assigned."
        icon={<DollarSign className="w-4 h-4 text-blue-600" />} rows={data.pay_groups || []}
        render={(r) => <span className="capitalize font-medium">{r.pay_group}</span>} />
      <Section title="Insurance Classification" testId="classification-section" empty="No classification assigned."
        icon={<ShieldAlert className="w-4 h-4 text-purple-600" />} rows={data.classifications || []}
        render={(r) => <span className="font-medium">{r.classification}{r.gl_code ? ` · GL ${r.gl_code}` : ''}{r.workers_comp_code ? ` · WC ${r.workers_comp_code}` : ''}</span>} />
      <Section title="Officer Pay Rates" testId="officer-rates-section" empty="No officer pay rates on file."
        icon={<DollarSign className="w-4 h-4 text-emerald-600" />} rows={data.officer_rates || []}
        render={(r) => <span className="font-medium">{r.job_number} · {r.billing_item_name} · {r.pay_rate == null ? '••••' : formatCurrency(r.pay_rate)}</span>} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PARENT — PAYROLL TAB
// ─────────────────────────────────────────────────────────────────────────
export default function PayrollTab({ employeeId, workState, employee, setEmployee }) {
  const perms = useEffectivePermissions();
  const [sp] = useSearchParams();
  const [sub, setSub] = useState(() => sp.get('sub') || 'compensation');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-blue-600" /> Payroll
        </CardTitle>
        <CardDescription>
          Compensation, Federal W-4, State Withholding, and Direct Deposit. Sensitive values
          (pay rate, account & routing numbers) are masked by default; every reveal is
          audit-logged. Executive-compensation rows are walled.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <PayDistFreqPanel employee={employee} setEmployee={setEmployee} />
        <Tabs value={sub} onValueChange={setSub} className="space-y-4">
          <TabsList className="flex flex-wrap">
            <TabsTrigger value="compensation" data-testid="payroll-subtab-compensation"><DollarSign className="w-4 h-4 mr-1.5" />Compensation</TabsTrigger>
            <TabsTrigger value="w4" data-testid="payroll-subtab-w4"><FileText className="w-4 h-4 mr-1.5" />Federal W-4</TabsTrigger>
            <TabsTrigger value="state" data-testid="payroll-subtab-state"><MapPin className="w-4 h-4 mr-1.5" />State Withholding</TabsTrigger>
            <TabsTrigger value="banking" data-testid="payroll-subtab-banking"><Landmark className="w-4 h-4 mr-1.5" />Direct Deposit</TabsTrigger>
            <TabsTrigger value="paygroups" data-testid="payroll-subtab-paygroups"><Layers className="w-4 h-4 mr-1.5" />Pay Groups &amp; Rates</TabsTrigger>
          </TabsList>
          <TabsContent value="compensation"><CompensationSubtab employeeId={employeeId} perms={perms} /></TabsContent>
          <TabsContent value="w4"><FederalW4Subtab employeeId={employeeId} /></TabsContent>
          <TabsContent value="state"><StateWithholdingSubtab employeeId={employeeId} workState={workState} /></TabsContent>
          <TabsContent value="banking"><BankingSubtab employeeId={employeeId} /></TabsContent>
          <TabsContent value="paygroups"><PayGroupsRatesSubtab employeeId={employeeId} /></TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
